import React, { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import logo from '../assets/logo.png'

const IntroAnimation = ({ onComplete }) => {
    const { t } = useTranslation()
    const [phase, setPhase] = useState(0)
    const [isVisible, setIsVisible] = useState(true)
    const videoRef = useRef(null)

    useEffect(() => {
        // Reset phase on mount (supports replays)
        setPhase(0)
        setIsVisible(true)

        const timers = []
        timers.push(setTimeout(() => setPhase(1), 800))
        timers.push(setTimeout(() => setPhase(2), 2000))
        timers.push(setTimeout(() => setPhase(3), 5000))
        timers.push(setTimeout(() => {
            sessionStorage.setItem('algomori-intro-shown', 'true')
            setIsVisible(false)
            onComplete?.()
        }, 6200))

        return () => timers.forEach(clearTimeout)
    }, [onComplete])

    if (!isVisible) return null

    const sloganWords = [
        t('intro.slogan.word1'),
        t('intro.slogan.word2'),
        t('intro.slogan.word3'),
        t('intro.slogan.word4'),
        t('intro.slogan.word5')
    ]

    return (
        <AnimatePresence>
            {isVisible && (
                <motion.div
                    className="fixed inset-0 z-[9999] flex items-center justify-center overflow-hidden"
                    initial={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                >
                    {/* Video Background */}
                    <video
                        ref={videoRef}
                        className="absolute inset-0 w-full h-full object-cover"
                        src="/nonoise.mp4"
                        autoPlay muted playsInline loop
                        style={{ filter: 'brightness(0.4) saturate(0.8)' }}
                    />

                    <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-black/70" />
                    <div className="absolute inset-0 bg-gradient-to-r from-black/20 via-transparent to-black/20" />

                    {/* Skip Button */}
                    <button
                        onClick={() => {
                            sessionStorage.setItem('algomori-intro-shown', 'true')
                            setIsVisible(false)
                            onComplete?.()
                        }}
                        className="absolute top-6 right-6 z-[10000] text-white/50 hover:text-white transition-colors p-2"
                        aria-label="Skip Animation"
                    >
                        <X size={24} />
                    </button>

                    {/* Content */}
                    <div className={`relative text-center px-6 max-w-4xl mx-auto ${phase >= 3 ? 'opacity-0 pointer-events-none transition-opacity duration-1000' : ''}`}>
                        {/* Logo */}
                        <motion.div
                            className="mb-10"
                            initial={{ opacity: 0, scale: 0.8, y: 10 }}
                            animate={phase >= 1 ? { opacity: 1, scale: 1, y: 0 } : {}}
                            transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
                        >
                            <img src={logo} alt="AlgoMori" className="w-[140px] h-auto object-contain brightness-0 invert opacity-85 mx-auto" />
                        </motion.div>

                        {/* Separator */}
                        <motion.div
                            className="w-16 h-px bg-white/30 mx-auto mb-10"
                            initial={{ scaleX: 0, opacity: 0 }}
                            animate={phase >= 1 ? { scaleX: 1, opacity: 1 } : {}}
                            transition={{ duration: 1.2, delay: 0.3 }}
                        />

                        {/* Slogan */}
                        <div className="flex flex-wrap justify-center gap-x-3 gap-y-2">
                            {sloganWords.map((word, i) => (
                                <motion.span
                                    key={i}
                                    className={`text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold tracking-tight leading-tight ${i < 2 ? 'text-primary-light' : 'text-white/95'
                                        }`}
                                    initial={{ opacity: 0, y: 20, filter: 'blur(8px)' }}
                                    animate={phase >= 2 ? { opacity: 1, y: 0, filter: 'blur(0px)' } : {}}
                                    transition={{ duration: 0.9, delay: i * 0.18, ease: [0.22, 1, 0.36, 1] }}
                                >
                                    {word}
                                </motion.span>
                            ))}
                        </div>

                        <motion.p
                            className="mt-8 text-white/40 text-sm sm:text-base tracking-[0.2em] uppercase font-light"
                            initial={{ opacity: 0, y: 10 }}
                            animate={phase >= 2 ? { opacity: 1, y: 0 } : {}}
                            transition={{ duration: 1, delay: 1.2 }}
                        >
                            {t('intro.subtitle.line1')}
                            <br />
                            {t('intro.subtitle.line2')}
                        </motion.p>
                    </div>

                    {/* Bottom line */}
                    <motion.div
                        className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-primary-light/60 to-transparent"
                        initial={{ scaleX: 0 }}
                        animate={phase >= 2 ? { scaleX: 1 } : {}}
                        transition={{ duration: 2, delay: 0.5 }}
                    />

                    {/* Exit: white curtain */}
                    {phase >= 3 && (
                        <motion.div
                            className="absolute inset-0 bg-white z-50"
                            initial={{ y: '100%' }}
                            animate={{ y: '0%' }}
                            transition={{ duration: 1.2, ease: [0.76, 0, 0.24, 1] }}
                        />
                    )}
                </motion.div>
            )}
        </AnimatePresence>
    )
}

export default IntroAnimation
